import React, { useState, useEffect, useRef, Fragment } from 'react';
import debounce from 'lodash/debounce';
import cn from 'classnames';
import { handleArrowMovement } from 'legacy/src/utility/accessibility';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import './index.scss';

export default function CharmBraceletPagination({
  children,
  currentQuestionIndex,
  showDoubleArrow,
  goToQuestion,
  onPreviousClick,
  onNextClick,
  showIcons = true,
  showArrows = true,
  isListView = false,
  focusedIndex = null,
  onClose,
}) {
  const [perPage, setPerPage] = useState(0);
  const [index, setIndex] = useState({ from: 0, to: 0 });
  const [hasFocusedSelectedAnswer, setHasFocusedSelectedAnswer] =
    useState(false);
  const questionIconsElement = useRef(null);
  const sizerElement = useRef(null);

  const hasPreviousPage = index.from > 0;
  const hasNextPage = index.to < children.length;

  const measure = debounce(() => {
    if (questionIconsElement.current) {
      // when list show all icons at the same time
      const questionPerPage = isListView
        ? children.length
        : Math.floor(
            questionIconsElement.current.clientWidth /
              sizerElement.current.clientWidth,
          );

      const initPage = Math.ceil((currentQuestionIndex + 1) / questionPerPage);

      setPerPage(questionPerPage);
      setIndex({
        from: (initPage - 1) * questionPerPage,
        to: initPage * questionPerPage,
      });
    }
  }, 50);

  const childrenForCurrentPage = children.slice(index.from, index.to);
  const closeButtonRef = useRef();

  useEffect(() => {
    // setup measures to correctly apply widths
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => measure(), [questionIconsElement.current, showDoubleArrow]);

  const nextClicked = () => {
    onNextClick();

    if (currentQuestionIndex >= index.to - 1) {
      setIndex({
        from: index.from + 1,
        to: index.to + 1,
      });
    }
  };
  const previousClicked = () => {
    onPreviousClick();

    if (currentQuestionIndex <= index.from) {
      // moveToPage(currentPage - 1);
      setIndex({
        from: index.from - 1,
        to: index.to - 1,
      });
    }
  };

  const moveToNextPage = () => {
    let nextFrom = index.from + perPage;
    let nextTo = index.to + perPage;

    if (nextTo >= children.length) {
      const delta = nextTo - children.length;
      nextTo -= delta;
      nextFrom -= delta;
    }

    setIndex({
      from: nextFrom,
      to: nextTo,
    });
    goToQuestion(nextFrom);
  };

  const moveToPreviousPage = () => {
    let prevFrom = index.from - perPage;
    let prevTo = index.to - perPage;

    if (prevFrom < 0) {
      const delta = Math.abs(prevFrom);
      prevFrom = 0;
      prevTo += delta;
    }

    setIndex({
      from: prevFrom,
      to: prevTo,
    });

    goToQuestion(prevFrom);
  };

  const questionIconsList =
    questionIconsElement.current && questionIconsElement.current.children;

  const focusSelectedAnswer = () => {
    if (!hasFocusedSelectedAnswer) {
      if (questionIconsList.length > 0 && questionIconsList[focusedIndex]) {
        questionIconsList[focusedIndex].focus();
        setHasFocusedSelectedAnswer(true);
      }
    }
  };

  const onBlur = () => {
    setHasFocusedSelectedAnswer(false);
  };

  const onKeyDown = handleArrowMovement({
    right: focusSelectedAnswer,
    left: focusSelectedAnswer,
  });

  return (
    <div className={cn('CharmBracelet', { '--list-view': isListView })}>
      {showIcons && (
        <div
          className="question-icon-list"
          ref={questionIconsElement}
          tabIndex={0}
          role="tablist"
          aria-label={`Questions List with ${children.length || 0} questions`}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
        >
          {childrenForCurrentPage}
        </div>
      )}

      <div
        className="question-icon-list-item --out-of-screen"
        ref={sizerElement}
      >
        {children[0]}
      </div>

      {showArrows && (
        <Fragment>
          <div className="actions" tabIndex={-1}>
            {showDoubleArrow && hasPreviousPage && (
              <PaginationArrow
                onClick={() => moveToPreviousPage()}
                ariaLabel="Skip back to the previous set of questions"
                icon="skip_back_arrow"
              />
            )}
            <PaginationArrow
              onClick={previousClicked}
              disabled={currentQuestionIndex <= 0}
              ariaLabel="Go to the previous question"
              icon="navigate_back"
            />

            <div className="item counters">
              {currentQuestionIndex + 1} of {children.length}
            </div>

            <PaginationArrow
              onClick={nextClicked}
              disabled={currentQuestionIndex >= children.length - 1}
              ariaLabel="Go to the next question"
              icon="navigate_next"
            />
            {showDoubleArrow && hasNextPage && (
              <PaginationArrow
                onClick={() => moveToNextPage()}
                ariaLabel="Skip ahead to next set of question"
                icon="skip_forward_arrow"
              />
            )}
          </div>
          {onClose && (
            <div className="flex items-center justify-center h-16.8 px-4 ml-auto">
              <ButtonWithIcon
                isIcon
                isNaked
                size="tiny"
                tabIndex={0}
                icon="close"
                onClick={onClose}
                aria-label="Close"
                ref={closeButtonRef}
              />
            </div>
          )}
        </Fragment>
      )}
    </div>
  );
}

const PaginationArrow = ({
  children,
  className,
  onClick,
  ariaLabel,
  disabled = false,
  icon,
}) => (
  <div className={cn('item', className)}>
    <ButtonWithIcon
      isIcon
      isPrimary
      size="small"
      icon={icon}
      iconClassName="text-white"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || ''}
      tabIndex={disabled ? -1 : 0}
    >
      {children}
    </ButtonWithIcon>
  </div>
);
